import React, { useMemo } from 'react';
import { CheckCircle, Mail, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const NOTIFY_TO = [
  'radhika.sharma@ab-inbev.com',
  'harsh.uttamchandani@ab-inbev.com',
];

const NOTIFY_CC = ['Mitchell.Bond@labatt.com'];

const val = (v) => {
  if (v === null || v === undefined || v === '') return 'N/A';
  return String(v);
};

/**
 * Build email body with all submitted campaign / POC / creative details.
 */
export function buildCampaignNotifyBody({
  campaignName,
  campaignId,
  brand,
  region,
  submitterEmail,
  campaignDetails = {},
  selectedPOCs = [],
  creativeDetails = {},
}) {
  const d = campaignDetails || {};
  const c = creativeDetails || {};
  const pocs = Array.isArray(selectedPOCs) ? selectedPOCs : [];

  const lines = [
    'A new campaign was submitted in SmartApp.',
    '',
    '--- Campaign Details ---',
    `Campaign ID: ${val(campaignId ?? d.campaignId)}`,
    `Campaign Name: ${val(campaignName || d.campaignName)}`,
    `Brand: ${val(brand || d.brand)}`,
    `Region: ${val(region || d.region)}`,
    `Campaign Type: ${val(d.platform)}`,
    `Objective: ${val(d.campaignObjective)}`,
    `Total Budget: ${d.totalBudget != null && d.totalBudget !== '' ? `$${d.totalBudget}` : 'N/A'}`,
    `Budget Type: ${val(d.budget_type)}`,
    `Start Date: ${val(d.startDate)}`,
    `End Date: ${val(d.endDate)}`,
    `GL Code: ${val(d.gl_code)}`,
    `Cost Centre: ${val(d.cost_centre)}`,
    `BDR Email: ${val(d.bdrEmail || d.bdr_email)}`,
    `Role: ${val(d.role)}`,
    `Territory: ${val(d.territory)}`,
    `Submitted by: ${val(submitterEmail)}`,
    '',
    `--- Selected POCs (${pocs.length}) ---`,
  ];

  if (pocs.length === 0) {
    lines.push('None');
  } else {
    pocs.forEach((poc, i) => {
      const name = poc.poc_name || poc.name || 'Unnamed POC';
      const id = poc.poc_id || poc.id || '';
      const budget =
        poc.poc_budget != null && poc.poc_budget !== ''
          ? `$${poc.poc_budget}`
          : 'N/A';
      lines.push(`${i + 1}. ${name}${id ? ` (${id})` : ''} - Budget: ${budget}`);
    });
  }

  const creativeDesc =
    c.creative_type === 'existing'
      ? c.existing_creative_description
      : c.creative_type === 'request'
        ? c.new_creative_description
        : c.new_creative_description || c.creative_description;

  lines.push(
    '',
    '--- Creative Details ---',
    `Creative Type: ${val(c.creative_type)}`,
    `Ad Headline: ${val(c.ad_headline)}`,
    `Body Copy: ${val(c.body_copy)}`,
    `Age Range: ${c.min_age || c.max_age ? `${val(c.min_age)} - ${val(c.max_age)}` : 'N/A'}`,
    `CTA: ${val(c.cta_type)}`,
    `Creative Description: ${val(creativeDesc)}`,
    `Additional Comment: ${val(c.additional_comment)}`
  );

  return lines.join('\n');
}

/**
 * Build a mailto: link with To / CC / subject / body pre-filled.
 */
export function buildCampaignNotifyMailto({
  campaignName,
  campaignId,
  brand,
  region,
  submitterEmail,
  campaignDetails,
  selectedPOCs,
  creativeDetails,
}) {
  // Outlook expects semicolons between recipients (not commas)
  const to = NOTIFY_TO.join(';');
  const subject = `Campaign submitted: ${campaignName || 'Untitled'}${campaignId ? ` (#${campaignId})` : ''}`;
  const body = buildCampaignNotifyBody({
    campaignName,
    campaignId,
    brand,
    region,
    submitterEmail,
    campaignDetails,
    selectedPOCs,
    creativeDetails,
  });

  const params = new URLSearchParams();
  params.set('subject', subject);
  params.set('body', body);
  params.set('cc', NOTIFY_CC.join(';'));

  // URLSearchParams encodes spaces as +; mailto clients prefer %20
  const query = params.toString().replace(/\+/g, '%20');
  return `mailto:${to}?${query}`;
}

/**
 * Campaign Submission Success Component
 * Displays a confirmation message after a campaign has been successfully submitted
 */
const CampaignSubmissionSuccess = ({
  campaignName,
  campaignId,
  brand,
  region,
  submitterEmail,
  campaignDetails,
  selectedPOCs,
  creativeDetails,
  onViewCampaigns,
  onCreateNew,
}) => {
  const mailtoHref = useMemo(
    () =>
      buildCampaignNotifyMailto({
        campaignName,
        campaignId,
        brand,
        region,
        submitterEmail,
        campaignDetails,
        selectedPOCs,
        creativeDetails,
      }),
    [
      campaignName,
      campaignId,
      brand,
      region,
      submitterEmail,
      campaignDetails,
      selectedPOCs,
      creativeDetails,
    ]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-white flex items-center justify-center p-6">
      <Card className="max-w-2xl w-full border-0 shadow-2xl">
        <CardContent className="p-12">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-green-400 rounded-full blur-2xl opacity-30 animate-pulse"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Campaign Submitted Successfully!
            </h1>
            <p className="text-lg text-gray-600">
              Your campaign <span className="font-semibold text-gray-900">"{campaignName}"</span> has been submitted for review.
            </p>
            {campaignId && (
              <p className="text-sm text-gray-500 mt-2">
                Campaign ID: <span className="font-mono font-semibold">#{campaignId}</span>
              </p>
            )}
          </div>

          {/* Information Cards */}
          <div className="space-y-4 mb-8">
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Under Review</h3>
                  <p className="text-sm text-gray-700">
                    Our team is reviewing your campaign submission. This typically takes 1-2 business days.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Notify team via mail</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Open a pre-filled email and click Send in Outlook to notify the team.
                  </p>
                  <Button asChild className="bg-purple-600 hover:bg-purple-700 text-white">
                    <a href={mailtoHref}>
                      <Mail className="w-4 h-4 mr-2" />
                      Notify team via mail
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={onCreateNew}
              variant="outline"
              className="flex-1 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 py-6 text-base font-semibold"
            >
              Create New Campaign
            </Button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            Need help? Contact us at{' '}
            <a href="mailto:radhika.sharma@ab-inbev.com" className="text-blue-600 hover:underline font-medium">
              radhika.sharma@ab-inbev.com
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignSubmissionSuccess;
